/**
 * Scrub Screen
 *
 * Static / structural data-integrity pass over the raw master pool (run before
 * filtering). Flags record-level problems — duplicates, bad dates, sign errors,
 * unbalanced TRF/CTR housekeeping, ID collisions, and document types with no
 * rollforward category mapping. This is the static counterpart to the
 * Rollforward reconciliation (which is the dynamic, day-over-day check).
 *
 * Run-on-demand: reads master, so it's gated on hasMaster. Results live in the
 * data slice for the session and can be recomputed at any time.
 */
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { calculateScrub } from "Redux/data/actions";

// Human-readable labels + severity for each check the backend can emit.
const CHECK_META = {
  duplicate: { label: "Exact duplicates", severity: "danger" },
  inv_id_collision: { label: "Invoice ID collision", severity: "danger" },
  unbalanced_transfer: { label: "Unbalanced transfer/contra", severity: "warning" },
  due_before_issue: { label: "Due date before issue", severity: "warning" },
  close_before_issue: { label: "Close date before issue", severity: "warning" },
  positive_receipt: { label: "Positive receipt/credit", severity: "warning" },
  unmapped_doctype: { label: "Unmapped document type", severity: "warning" },
};

const metaFor = (name) => {
  if (name.startsWith("missing:"))
    return { label: `Missing ${name.slice(8)}`, severity: "danger" };
  return CHECK_META[name] || { label: name, severity: "info" };
};

const tagClass = (severity) =>
  severity === "danger" ? "is-danger" : severity === "warning" ? "is-warning" : "is-info";

// -------------------------------------------------------------
const Scrub = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { scrub } = useSelector((state) => state.data);
  const { hasMaster } = useSelector((state) => state.project);
  const dispatch = useDispatch();

  const runScrub = () => {
    setIsLoading(true);
    dispatch(calculateScrub(() => setIsLoading(false)));
  };

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet. Build the master data first, then scrub it.
      </div>
    );

  return (
    <div>
      {/* Header + run button */}
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1rem" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: "0.95rem", color: "var(--ev-text)" }}>
            Data Scrub
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--ev-text-muted)" }}>
            Record-level integrity checks over the raw master pool, before filtering.
          </div>
        </div>
        <button
          className={`button is-small ${isLoading ? "is-loading" : ""}`}
          onClick={runScrub}
          disabled={isLoading}
          style={{
            backgroundColor: "#79589F", color: "#fff", border: "none",
            fontWeight: 700, borderRadius: "8px",
          }}
        >
          <i className="fa-duotone fa-broom" style={{ marginRight: "0.4rem" }} />
          {scrub ? "Re-run Scrub" : "Run Scrub"}
        </button>
      </div>

      {isLoading && (
        <div style={{ fontSize: "0.85rem", color: "var(--ev-text-muted)", fontStyle: "italic", padding: "1rem 0" }}>
          Scrubbing master data...
        </div>
      )}

      {!isLoading && !scrub && (
        <div className="notification is-info is-light" style={{ fontSize: "0.82rem" }}>
          Run the scrub to check the master pool for duplicates, bad dates, sign errors,
          unbalanced transfers, and unmapped document types.
        </div>
      )}

      {!isLoading && scrub && (
        <>
          {/* Summary */}
          <div style={{ marginBottom: "1rem", fontSize: "0.82rem", color: "var(--ev-text)" }}>
            <strong>{scrub.flagged.toLocaleString()}</strong> record{scrub.flagged === 1 ? "" : "s"} flagged.
          </div>

          {scrub.summary && scrub.summary.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1.25rem" }}>
              {scrub.summary.map((s) => {
                const m = metaFor(s.check);
                return (
                  <span
                    key={s.check}
                    className={`tag ${tagClass(m.severity)} is-light`}
                    style={{ borderRadius: "6px", fontSize: "0.72rem", fontWeight: 600 }}
                  >
                    {m.label}
                    <span className="tag is-dark is-small" style={{ marginLeft: "0.4rem", borderRadius: "4px" }}>
                      {s.count.toLocaleString()}
                    </span>
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="notification is-success is-light" style={{ fontSize: "0.82rem" }}>
              No integrity issues found — the master pool passed all scrub checks.
            </div>
          )}

          {/* Flagged records table */}
          {scrub.rows && scrub.rows.length > 0 && (
            <div className="table-container">
              <div style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", marginBottom: "0.4rem" }}>
                Showing {scrub.rows.length.toLocaleString()} of {scrub.flagged.toLocaleString()} flagged rows
              </div>
              <table className="table is-fullwidth is-striped is-bordered is-small-table">
                <thead>
                  <tr>
                    {scrub.columns.map((h) => (
                      <th key={h} style={{ whiteSpace: "nowrap", fontSize: "0.7rem" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {scrub.rows.map((row, i) => (
                    <tr key={i}>
                      {scrub.columns.map((h) => (
                        <td
                          key={`${i}-${h}`}
                          style={{
                            whiteSpace: "nowrap",
                            fontSize: "0.72rem",
                            color: h === "scrub_flags" ? "var(--ev-danger)" : undefined,
                            fontWeight: h === "scrub_flags" ? 600 : undefined,
                          }}
                        >
                          {row[h]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Scrub;
